<template>
  <div
    :class="controlClasses"
    class="mds-form-control mds-date-custom-format-input"
  >
    <label :for="uid" :style="labelStyles" v-if="label">
      {{ label }}
      <span v-if="showAsterisk" class="asterisk">*</span>
    </label>

    <div
      :style="innerStyles"
      class="inner"
      :class="{ 'is-focused': calendarVisible }"
    >
      <div class="control-row">
        <input
          :disabled="disabled"
          :ref="uid"
          :id="uid"
          :placeholder="placeholder"
          type="text"
          @focus="onFocus"
          :value="formattedLocalDate"
          readonly
        />

        <button v-if="showClear" @click="onClear" class="clear" type="button">
          <VIcon material="cancel" />
        </button>

        <VIcon
          v-if="showCalendarIcon"
          class="calendar-icon"
          material="calendar_today"
          @click="onClickIcon"
        />
      </div>

      <div class="messages-row">
        <ul class="error" v-if="validation.isValid === false">
          <li
            v-for="(error, index) in validation.errors"
            :key="'error' + index"
          >
            {{ error }}
          </li>
        </ul>

        <ul class="warning" v-if="validation.warnings.length > 0">
          <li
            v-for="(warning, index) in validation.warnings"
            :key="'warning' + index"
          >
            {{ warning }}
          </li>
        </ul>

        <div class="success" v-if="validation.success !== null">
          {{ validation.success }}
        </div>

        <div class="helper-text" v-if="hasHelperTextSlot">
          <!-- @slot Слот вспомогательного текста -->
          <slot name="helper-text" />
        </div>
      </div>
    </div>

    <VPopup
      class="mds-date-input-popup"
      :target="$refs[uid]"
      :visible="calendarVisible"
      @update:visible="calendarVisible = false"
    >
      <div class="mds-date-input__calendar">
        <div
          v-if="showCalendarControls"
          class="mds-date-range-input__calendar__form"
        >
          <div class="inputs">
            <div class="inputs-item">
              <label>{{ label }}</label>
              <VInputMask
                ref="popup-date-from-input"
                :disabled="disabled"
                :clearable="clearable"
                :placeholder="placeholder"
                :mask="Utils.date.dateMask"
                :value="defaultFormattedLocalDate"
                raw
                @input="onInput($event)"
              />
            </div>
          </div>

          <div class="buttons">
            <button class="mds-btn" @click="onClear">Очистить</button>
          </div>
        </div>

        <VMonthRenderer
          v-for="(m, index) of monthCount"
          :key="uid + '_' + index"
          :first-day-of-week="1"
          :is-first="index === 0"
          :is-last="index + 1 === showMonths"
          :is-selected="isSelected"
          :month="dataForMonthRender[index].month"
          :year="dataForMonthRender[index].year"
          @change-month="onChangeMonth"
          @select-day="onSelectDay"
          @wheel.prevent="onMonthScroll"
        />
      </div>
    </VPopup>
  </div>
</template>

<style lang="scss" src="./VDateCustomFormatInput.scss" />

<script lang="ts">
import DateSimple   from "@/core/types/DateSimple";
import Utils        from "@/core/utils/Utils";
import VBaseControl from "@/core/services/VBaseControl";
import Constants from "@/core/misc/Constants";
import ProductionPlansService from "@/domains/ProductionPlans/ProductionPlansService";

export default defineComponent({
  name: "VDateCustomFormatInput",

  extends: VBaseControl,

  emits: ['input'],

  props: {
    // Базовые пропы (placeholder, disabled, disabledReasons, label, labelWidth,
    // layout, hideAsterisk, required, email, name, phone, min, max, minLength,
    // maxLength, validateMethod, warningMethod, successMethod) — из VBaseControl.

    /** Иконка */
    icon: {
      type:    String,
      default: null,
    },
    /** Кнопка очистки */
    clearable: {
      type:    Boolean,
      default: false,
    },
    /** Модель — Date, валидная строка, null/undefined, или невалидная строка */
    value: {
      type:    [Date, String],
      default: null,
    },
    /** Количество месяцев в попапе */
    showMonths: {
      type:    Number,
      default: 2,
    },
    /** Выравнивание текста */
    textAlign: {
      type:      String,
      default:   null,
      validator: (value: string) => Constants.TEXT_ALIGNS.includes(value),
    },
    /** Формат отображения, например DD|MM|yyyy */
    format: {
      type:    String,
      default: "DD|MM|yyyy",
    },
    /** Режим отображения: 'date' или 'month' */
    mode: {
      type:      String,
      default:   'date',
      validator: (value: string) => ['date', 'month'].includes(value),
    },
    showCalendarControls: {
      type:    Boolean,
      default: false,
    },
  },

  data() {
    return {
      Utils,
      calendarVisible: false,
      localDate:    null,
      invalidValue: null,
      /**
       * Переменная нужна для навигации по календарю.
       * Скролл вперёд — дата увеличивается, назад — уменьшается.
       */
      visibleDate: null,
    };
  },

  computed: {
    defaultFormattedLocalDate() {
      return Utils.date.format(this.localDate);
    },

    /** Расширяем базовые классы своими. */
    controlClasses(): string[] {
      const classes = this.getBaseControlClasses();
      if (this.clearable === true) classes.push("clearable");
      if (this.icon !== null)      classes.push("with-icon");
      return classes;
    },

    showClear(): boolean {
      return this.clearable === true
        && (this.localDate !== null || this.invalidValue !== null)
        && this.disabled === false;
    },

    showCalendarIcon(): boolean {
      return (this.clearable === true && (this.localDate !== null || this.invalidValue !== null)) === false;
    },

    labelStyles() {
      if (this.labelWidth === null) return null;
      return { width: this.labelWidth };
    },

    innerStyles() {
      const result: Record<string, string> = {};
      if (this.labelWidth !== null) result["width"] = `calc(100% - ${this.labelWidth})`;
      if (this.textAlign  !== null) result["text-align"] = this.textAlign;
      return result;
    },

    monthCount() {
      return new Array(this.showMonths).fill(null);
    },

    hasIconSlot(): boolean {
      return this.$slots.icon !== undefined;
    },

    hasHelperTextSlot(): boolean {
      return this.$slots["helper-text"] !== undefined;
    },

    dataForMonthRender() {
      // Защита от null visibleDate.
      const baseDate = this.visibleDate !== null ? this.visibleDate : new Date();
      return new Array(this.showMonths).fill(null).map((item, index) => {
        let month = baseDate.getMonth() + index;
        let year  = baseDate.getFullYear();
        if (month > 11) {
          month = 0;
          year += 1;
        }
        return { month, year };
      });
    },

    formattedLocalDate() {
      // Невалидное значение — показываем его как есть.
      if (this.invalidValue !== null) return this.invalidValue;

      if (this.localDate instanceof Date) {
        // Режим "month" — только месяц и год.
        if (this.mode === 'month') {
          return `${ProductionPlansService.months[this.localDate.getMonth()]} ${this.localDate.getFullYear()}`;
        }

        // Обычный режим с датой.
        if (this.localDate.toString().match(/^\D/i)) {
          return Utils.string.capitalize(Utils.date.format(this.localDate, this.format));
        }

        return Utils.date.format(this.localDate, this.format);
      }
      return null;
    },
  },

  watch: {
    value(value) {
      this.parseValue(value);
    },
    localDate(value) {
      this.validate();
      if (value instanceof Date) {
        this.visibleDate = new DateSimple(value).startOfMonth();
      } else if (this.visibleDate === null) {
        // Если localDate стал null и visibleDate тоже null — ставим текущую дату.
        this.visibleDate = DateSimple.today().startOfMonth();
      }
    },
  },

  created() {
    this.parseValue(this.value);
    if (this.localDate !== null) {
      this.visibleDate = new DateSimple(this.localDate).startOfMonth();
    } else {
      this.visibleDate = DateSimple.today().startOfMonth();
    }
  },

  methods: {
    /** Парсит входящее значение, определяет валидное оно или нет */
    parseValue(value) {
      this.invalidValue = null;

      if (value instanceof Date) {
        this.localDate = value;
      } else if (typeof value === "string") {
        const parsedDate = Date.parse(value);
        if (isNaN(parsedDate) === false) {
          this.localDate = new Date(value);
        } else {
          // Невалидная строка — сохраняем как есть.
          this.invalidValue = value;
          this.localDate = null;
        }
      } else {
        this.localDate = null;
      }
    },

    onInput(event) {
      if (event.fullfilled === false) return;
      const date = event.masked;

      if (Utils.date.validInputString(date) === true) {
        const _date = new DateSimple(date);
        this.localDate    = Utils.date.toDayStart(_date);
        this.invalidValue = null;
      } else {
        this.localDate = null;
      }

      this.$emit("input", this.localDate);
    },

    onClickIcon(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this.$refs[this.uid]) {
        this.$refs[this.uid].focus();
      }
    },

    toggleCalendar(value) {
      if (value === true) {
        window.addEventListener("keydown", this.keyDownListeners);
      } else {
        window.removeEventListener("keydown", this.keyDownListeners);
      }
      this.calendarVisible = value;
    },

    onChangeMonth(data) {
      if (data.direction === "next") {
        this.visibleDate = this.visibleDate.addMonths(1);
      } else {
        this.visibleDate = this.visibleDate.addMonths(-1);
      }
    },

    onMonthScroll(event) {
      const delta = event.deltaY || event.detail || event.wheelDelta;
      this.onChangeMonth({ direction: delta < 0 ? "previous" : "next" });
    },

    onSelectDay(date) {
      this.validation.markDirty();

      // Режим "month" — устанавливаем первый день месяца.
      if (this.mode === 'month') {
        this.localDate = new Date(date.getFullYear(), date.getMonth(), 1);
      } else {
        this.localDate = date;
      }

      this.invalidValue = null;
      this.$emit("input", this.localDate);
      this.toggleCalendar(false);
    },

    isSelected(date) {
      return Utils.date.format(this.localDate) === Utils.date.format(date);
    },

    onClear() {
      this.localDate    = null;
      this.invalidValue = null;
      this.$emit("input", this.localDate);
    },

    onFocus() {
      this.validation.markTouched();
      this.toggleCalendar(true);
    },

    /**
     * Запускает валидацию текущего значения через Validation.
     * Гейт touched+dirty — контрол сам решает, когда показывать ошибки.
     *
     * Поверх стандартных правил применяем доп. проверку: если пользователь
     * ввёл невалидную строку (не разобралась в дату) — это отдельная ошибка
     * формата, не покрываемая базовыми правилами Validation.
     */
    async validate(): Promise<void> {
      if (this.validation.isTouched === false || this.validation.isDirty === false) return;

      await this.validation.validate(Utils.date.cloneDate(this.localDate));

      this.validateFormat();
    },

    /**
     * Доп. правило: пользователь не должен оставлять невалидный текст.
     *
     * Если parseValue не смог распарсить ввод — мы сохранили исходную строку
     * в invalidValue и обнулили localDate. Это значит, что внешне поле что-то
     * показывает, но семантически даты нет. Пушим явную ошибку формата.
     */
    validateFormat(): void {
      if (this.invalidValue === null) return;

      this.validation.errors.push("Некорректный формат даты");
      this.validation.isValid = false;
    },

    keyDownListeners(event) {
      if (event.key === "Escape" || event.key === "Tab") {
        const input = document.activeElement;
        if (input && typeof input.blur === "function") input.blur();
        this.toggleCalendar(false);
      }
    },
  },
});
</script>
